import { Box, Button, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React from 'react';
import { ProductOnUser } from 'common-ts/dist/models/Product';
import { formatRupiah, hargaLangsung, marketplaceComparison } from './LinkPageShared';

// Template B of the /link page top segment: a horizontal snap-scroll product
// carousel whose cards lead with the "Harga Marketplace vs Harga Langsung"
// comparison — the reason to buy here instead of leaving to a marketplace.

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  scroller: {
    display: 'flex',
    gap: '12px',
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    padding: '4px 2px 10px',
    // Hide the scrollbar without losing scrollability.
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  card: {
    scrollSnapAlign: 'start',
    flex: '0 0 46%',
    maxWidth: '46%',
    backgroundColor: '#ffffff',
    border: '1px solid #ececec',
    borderRadius: '12px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  cardImage: {
    width: '100%',
    aspectRatio: '1 / 1',
    objectFit: 'cover',
    display: 'block',
    cursor: 'pointer',
  },
  cardBody: {
    padding: '8px 10px 10px',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  cardTitle: {
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: 1.3,
    marginBottom: '6px',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  comparisonLabel: {
    fontSize: '10px',
    color: '#8a8a8a',
  },
  marketplacePrice: {
    fontSize: '12px',
    color: '#8a8a8a',
    textDecoration: 'line-through',
  },
  directLabel: {
    fontSize: '10px',
    color: '#0a7d43',
    fontWeight: 700,
    marginTop: '4px',
  },
  directPrice: {
    fontSize: '16px',
    fontWeight: 700,
  },
  hematBadge: {
    display: 'inline-block',
    alignSelf: 'flex-start',
    backgroundColor: '#e7f6ee',
    color: '#0a7d43',
    fontSize: '10px',
    fontWeight: 700,
    borderRadius: '6px',
    padding: '2px 6px',
    margin: '4px 0px 2px',
  },
  buyButton: {
    marginTop: 'auto',
    paddingTop: '6px',
  },
  buyButtonInner: {
    width: '100%',
    borderRadius: '8px',
    fontWeight: 700,
    fontSize: '12px',
    padding: '7px 0px',
  },
  soldOut: {
    fontSize: '11px',
    color: '#b00020',
    fontWeight: 600,
    margin: '6px 0px',
  },
}),
);

interface LinkPageProductCarouselProps {
  products: ProductOnUser[];
  onBuyProduct: (product: ProductOnUser) => void;
}

export default function LinkPageProductCarousel({ products, onBuyProduct }: LinkPageProductCarouselProps) {
  const classes = useStyles();

  if (!products.length) return null;

  return (
    <Box className={classes.scroller}>
      {
        products.map((product) => {
          const comparison = marketplaceComparison(product);
          const direct = hargaLangsung(product);
          return (
            <Box key={product.productId} className={classes.card}>
              <img
                className={classes.cardImage}
                src={product.displayImage}
                alt={product.name}
                onClick={() => { if (!product.isSoldOut) onBuyProduct(product); }}
              />
              <Box className={classes.cardBody}>
                <Typography
                  className={classes.cardTitle}
                  onClick={() => { if (!product.isSoldOut) onBuyProduct(product); }}
                >
                  {product.name}
                </Typography>
                {
                  comparison ?
                  <>
                    <Typography className={classes.comparisonLabel}>Harga Marketplace</Typography>
                    <Typography className={classes.marketplacePrice}>
                      {formatRupiah(comparison.hargaMarketplace)}
                    </Typography>
                    <Typography className={classes.directLabel}>Harga Langsung</Typography>
                    <Typography className={classes.directPrice}>
                      {formatRupiah(comparison.hargaLangsung)}
                    </Typography>
                    <Typography className={classes.hematBadge}>
                      Hemat {formatRupiah(comparison.hemat)} ({comparison.hematPercent}%)
                    </Typography>
                  </> :
                  <Typography className={classes.directPrice}>
                    {formatRupiah(direct)}
                  </Typography>
                }
                {product.isSoldOut && <Typography className={classes.soldOut}>Stok Habis</Typography>}
                <Box className={classes.buyButton}>
                  <Button
                    className={classes.buyButtonInner}
                    variant="contained"
                    color="primary"
                    disableElevation
                    disabled={!!product.isSoldOut}
                    onClick={() => onBuyProduct(product)}
                  >
                    Beli Langsung
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })
      }
    </Box>
  );
}
